import { QueryResult } from '@apollo/client'
import { createStrictContext } from 'utils/createContext'
import {
  GetDocumentChecklistItemsMetadataQuery,
  GetDocumentChecklistItemsQuery,
  useGetDocumentChecklistItemsMetadataQuery,
  useGetDocumentChecklistItemsQuery,
} from 'schema'
import { useGlobalState } from 'context/GlobalStateContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'

type GetDocumentChecklistItemsQueryResult =
  QueryResult<GetDocumentChecklistItemsQuery>
export type DocumentChecklistItem =
  GetDocumentChecklistItemsQuery['documentChecklistItems']['records'][0]
type GetDocumentChecklistItemsMetadataQueryResult =
  QueryResult<GetDocumentChecklistItemsMetadataQuery>
type MetadataConcreteMaterials = NonNullable<
  GetDocumentChecklistItemsMetadataQueryResult['data']
>['project']['concreteMaterials']
type MetadataConcreteTestSets = NonNullable<
  GetDocumentChecklistItemsMetadataQueryResult['data']
>['project']['concreteTestSets']

const { Provider, useContextFn } = createStrictContext<{
  records: DocumentChecklistItem[]
  refetch: GetDocumentChecklistItemsQueryResult['refetch']
  fetching: GetDocumentChecklistItemsQueryResult['loading']
  metadata: {
    concreteMaterials: MetadataConcreteMaterials
    concreteTestSets: MetadataConcreteTestSets
  }
  selectOptions: {
    placementTypesOptions: DefaultOptionType[]
  }
}>('DocumentChecklistTableContext')

export const useDocumentChecklistsTableContext = useContextFn

export const DocumentChecklistsTableProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    refetch,
    loading: fetching,
  } = useGetDocumentChecklistItemsQuery({
    ...DefaultQueryHookOptions,
    variables: {
      documentChecklistItemListInput: {
        projectId: { equals: projectId },
      },
    },
  })
  const { data: metadata } = useGetDocumentChecklistItemsMetadataQuery({
    ...DefaultQueryHookOptions,
    variables: {
      globalId: projectId!,
    },
    skip: !projectId,
  })
  const placementTypesOptions = useOptions({
    items: metadata?.project?.concreteMaterials,
    value: 'globalId',
    label: 'placementType',
    sort: 'placementType',
  })
  return (
    <Provider
      value={{
        records: data?.documentChecklistItems?.records ?? [],
        refetch,
        fetching,
        metadata: {
          concreteMaterials: metadata?.project?.concreteMaterials ?? [],
          concreteTestSets: metadata?.project?.concreteTestSets ?? [],
        },
        selectOptions: {
          placementTypesOptions,
        },
      }}
    >
      {children}
    </Provider>
  )
}
